import utils from 'helpers/utils';
import dom from 'helpers/dom';

const selectors = {
  button: 'button',
  field: 'input:not([type="hidden"]), textarea',
  row: '.MuiTableBody-root .MuiTableRow-root',
  checkbox: 'input[type="checkbox"]',
  listItem: '[data-list-index]',
  page: '.Page',
  pressable: '.Button, .IconButton',
  skeleton: '.MuiSkeleton-root'
};

function isOnScreen (ele) {
  const bbox = dom.getBbox(ele, false);
  return bbox.width > 0 && bbox.height > 0 &&
    bbox.bottom > 0 && bbox.right > 0 &&
    bbox.top < window.innerHeight && bbox.left < window.innerWidth;
}

function isEnabled (ele) {
  return Boolean(ele) && !ele.disabled && !`${ele.className}`.includes('Mui-disabled');
}

function findRoots (target) {
  if (utils.isString(target)) {
    return [...document.querySelectorAll(target)];
  } else {
    return utils.toArray(target ?? document).filter((root) => {
      return Boolean(root);
    });
  }
}

function findAll (target, selector) {
  return findRoots(target).reduce((a, root) => {
    return a.concat(root.matches?.(selector) ? [root] : [...root.querySelectorAll(selector)]);
  }, []);
}

function findElements (target, options = {}) {
  const {label, field = false, self = false} = options;
  const found = self ? findRoots(target) : findAll(target, field ? selectors.field : selectors.button);

  return found.filter((el) => {
    return el.getAttribute('aria-hidden') !== 'true' &&
      (!utils.isDefined(label) || utils.matchText(el.textContent.trim(), label));
  });
}

function findElement (target, options = {}) {
  const {index = 0, onScreen = false} = options;
  const found = findElements(target, options).filter((el) => {
    return !onScreen || isOnScreen(el);
  });
  const el = found[index < 0 ? found.length + index : index] ?? null;

  return (el && isEnabled(el) && !el.readOnly) ? el : null;
}

function findRows (target) {
  return findAll(target, selectors.row);
}

function findRow (target, index) {
  const rows = findRows(target);
  const indexed = rows.some((row) => {
    return row.hasAttribute('data-index');
  });

  if (indexed) {
    return rows.find((row) => {
      return +row.getAttribute('data-index') === +index;
    }) ?? null;
  } else {
    return rows[index] ?? null;
  }
}

function findRowCheckbox (target, index = null) {
  if (utils.isDefined(index)) {
    return findRow(target, index)?.querySelector(selectors.checkbox) ?? null;
  } else {
    return findAll(findRows(target), selectors.checkbox)[0] ?? null;
  }
}

function findListItems (target) {
  return findRoots(target).reduce((a, root) => {
    return a.concat([...root.querySelectorAll(selectors.listItem)].filter((item) => {
      const parent = item.parentElement?.closest(selectors.listItem);
      return !parent || !root.contains(parent);
    }));
  }, []);
}

function isPressable (ele) {
  return ele.matches(selectors.pressable) && !ele.closest(selectors.listItem);
}

function lastListItems (target, count) {
  return findListItems(target)
    .sort((a, b) => {
      return +b.getAttribute('data-list-index') - +a.getAttribute('data-list-index');
    })
    .slice(0, count);
}

function hasSkeleton (target = null) {
  return findRoots(target).some((root) => {
    return Boolean(root.matches?.(selectors.skeleton) || root.querySelector(selectors.skeleton));
  });
}

async function waitForPage (options, cancelled) {
  const {poll, max} = options;
  let shown = false;
  let elapsed = 0;

  while (!shown && elapsed < max && !cancelled()) {
    await utils.sleep(poll);
    elapsed += poll;
    shown = Boolean(document.querySelector(selectors.page));
  }

  return !cancelled();
}

function setValue (ele, value) {
  const prototype = ele.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  setter?.call(ele, value);
  ele.dispatchEvent(new Event('input', {bubbles: true}));
}

const walk = {
  isOnScreen,
  isEnabled,
  findElements,
  findElement,
  findRows,
  findRow,
  findRowCheckbox,
  isPressable,
  lastListItems,
  hasSkeleton,
  waitForPage,
  setValue
};

export default walk;
